import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Button, Container, Nav, Offcanvas } from 'react-bootstrap';
import { List, Bag } from 'react-bootstrap-icons';
import { useCarrito } from '../ventaUnidad/componenetes/CarritoContext';
import CarUni from '../ventaUnidad/componenetes/carUni';
import { useNavigate, useParams, Link } from 'react-router-dom';
import logo from '../../../utils/cr1-logo.png';
import { useTiendaConfig } from '../../../hooks/useTiendaConfig';
import getIcon from '../../../utils/getIcon';
import { useSucursal } from '../../../contexts/SucursalContext';

function NavbarUni() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [cartBounce, setCartBounce] = useState(false);
    const [show, setShow] = useState(false);
    const [showFloatingCartButton, setShowFloatingCartButton] = useState(false);
    const [floatingPosition, setFloatingPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const navbarRef = useRef(null);
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const { socialLinks } = useTiendaConfig();
    const { carrito } = useCarrito();

    const closeCar = () => setShow(false);
    const showCar = () => setShow(true);
    const totalItems = carrito.reduce((total, item) => total + item.quantity, 0);
    const navigate = useNavigate();
    //Sucursal Provider
    const { idSucursal: contextIdSucursal } = useSucursal(); // Obtén el idSucursal del contexto
    const { idSucursal: paramIdSucursal } = useParams();

    // Usa el idSucursal de los parámetros de la URL o, como respaldo, el del contexto
    const idSucursal = paramIdSucursal || contextIdSucursal;

    // Si aún no hay un idSucursal válido, redirige a una sucursal predeterminada o muestra un error
    if (!idSucursal) {
        console.warn('No se encontró idSucursal. Redirigiendo a la sucursal predeterminada...');
        navigate('/1'); // Cambia "1" por el idSucursal predeterminado
    }

    const redirectToRoot = () => {
        if (!idSucursal) {
            console.error('No se puede redirigir porque idSucursal es undefined.');
            return;
        }
        navigate(`/${idSucursal}`);
    };

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    useEffect(() => {
        if (totalItems > 0) {
            setCartBounce(true);
            setTimeout(() => setCartBounce(false), 500);
        }
    }, [totalItems]);

    useEffect(() => {
        const updateFloatingCartVisibility = () => {
            if (!navbarRef.current) return;

            const navbarRect = navbarRef.current.getBoundingClientRect();
            const isNavbarVisible = navbarRect.bottom > 0;
            setShowFloatingCartButton(!isNavbarVisible);
        };

        const initializeFloatingPosition = () => {
            const buttonSize = 45;
            const initialX = Math.max(0, window.innerWidth - buttonSize);
            const initialY = Math.max(0, window.innerHeight / 2 - buttonSize / 2);
            setFloatingPosition({ x: initialX, y: initialY });
        };

        initializeFloatingPosition();
        updateFloatingCartVisibility();
        window.addEventListener('scroll', updateFloatingCartVisibility, { passive: true });
        window.addEventListener('resize', () => {
            updateFloatingCartVisibility();
            initializeFloatingPosition();
        });

        return () => {
            window.removeEventListener('scroll', updateFloatingCartVisibility);
            window.removeEventListener('resize', updateFloatingCartVisibility);
        };
    }, []);

    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (event) => {
            const buttonSize = 45;
            const nextX = event.clientX - dragOffsetRef.current.x;
            const nextY = event.clientY - dragOffsetRef.current.y;

            const clampedX = Math.min(Math.max(0, nextX), window.innerWidth - buttonSize);
            const clampedY = Math.min(Math.max(0, nextY), window.innerHeight - buttonSize);

            setFloatingPosition({ x: clampedX, y: clampedY });
        };

        const stopDragging = () => {
            setFloatingPosition((prevPosition) => {
                const buttonSize = 45;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const spaceLeft = prevPosition.x;
                const spaceRight = viewportWidth - prevPosition.x - buttonSize;

                const shouldSnapRight = spaceRight <= spaceLeft;
                const nextX = shouldSnapRight
                    ? viewportWidth - buttonSize
                    : 0;
                const nextY = Math.min(Math.max(0, prevPosition.y), viewportHeight - buttonSize);

                return { x: nextX, y: nextY };
            });
            setIsDragging(false);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
        };
    }, [isDragging]);

    const handleFloatingButtonPointerDown = (event) => {
        if (event.button !== 0) return;

        const rect = event.currentTarget.getBoundingClientRect();
        dragOffsetRef.current = {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        };

        setIsDragging(true);
    };

    // Esta función se asegura de que siempre obtengas una URL válida de WhatsApp
    

    const isActive = (path) => {
        const currentPath = window.location.pathname;
        
        // Resaltar "Inicio" solo para la URL exacta
        if (path === `/${idSucursal}`) {
            return currentPath === path || currentPath === '/';
        }
        
        // Resaltar "Categorías" para la ruta exacta y para rutas de productos
        if (path === `/${idSucursal}/Catalogo`) {
            return currentPath === path || currentPath.startsWith(`/${idSucursal}/Productos`);
        }
        
        // Resaltar rutas específicas (Sobre Nosotros, Contacto, Preguntas Frecuentes)
        if (path === `/${idSucursal}/SobreNosotros` ||
            path === `/${idSucursal}/Contacto` ||
            path === `/${idSucursal}/PreguntaFrecuentes`) {
            return currentPath === path;
        }
        
        return false;
    };

    return (
        <div>
            <Navbar ref={navbarRef} expand='lg' className="color-bg-primary py-3">
                <Container fluid className="position-relative mx-0 mx-md-4">

                    {/* ================= MOBILE ================= */}
                    <div className="d-flex d-lg-none align-items-center justify-content-between w-100">

                        {/* Burger izquierda */}
                        <Button
                            variant='link'
                            onClick={toggleMenu}
                            aria-controls='offcanvasNavbar'
                            className='color-secundary p-0'
                        >
                            <List size={26} />
                        </Button>

                        {/* Logo centro */}
                        <Navbar.Brand as={Link} to={`/${idSucursal}`} className='m-0'>
                            <img
                                src={logo}
                                width='110'
                                alt='Aurora Bags'
                                title='Aurora Bags'
                                style={{ objectFit: 'cover' }}
                            />
                        </Navbar.Brand>

                        {/* Carrito derecha */}
                        <button
                            onClick={showCar}
                            title='Carrito de compra'
                            className={`cart-button-minimal ${cartBounce ? 'cart-bounce' : ''}`}
                        >
                            <Bag size={22} className='color-secundary' />

                            {totalItems > 0 && (
                                <span className="cart-badge">
                                    {totalItems}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* ================= DESKTOP ================= */}
                    <div className="d-none d-lg-flex align-items-center justify-content-between w-100">

                        {/* Logo izquierda */}
                        <Navbar.Brand as={Link} to={`/${idSucursal}`} className='m-0'>
                            <img
                                src={logo}
                                width='140'
                                alt='Aurora Bags'
                                title='Aurora Bags'
                                style={{ objectFit: 'cover' }}
                            />
                        </Navbar.Brand>

                        {/* Menu centro */}
                        <Nav className='d-flex align-items-center position-absolute start-50 translate-middle-x'>

                            <Button
                                className={`nav-link text-danger fw-semibold fs-6 ms-3 ${isActive(`/${idSucursal}`) ? 'active-nav-link' : ''
                                    }`}
                                onClick={redirectToRoot}
                            >
                                Inicio
                            </Button>

                            <Link
                                to={`/${idSucursal}/Catalogo`}
                                className='text-decoration-none ms-3'
                            >
                                <Button
                                    className={`nav-link color-secundary fw-semibold fs-6 ${isActive(`/${idSucursal}/Catalogo`)
                                        ? 'active-nav-link'
                                        : ''
                                        }`}
                                >
                                    Categorias
                                </Button>
                            </Link>
                            <Link
                                to={`/${idSucursal}/SobreNosotros`}
                                className='text-decoration-none ms-3'
                            >
                                <Button
                                    className={`nav-link color-secundary fw-semibold fs-6 ${isActive(`/${idSucursal}/SobreNosotros`)
                                        ? 'active-nav-link'
                                        : ''
                                        }`}
                                >
                                    Sobre Nosotros
                                </Button>
                            </Link>
                            <Link
                                to={`/${idSucursal}/Contacto`}
                                className='text-decoration-none ms-3'
                            >
                                <Button
                                    className={`nav-link color-secundary fw-semibold fs-6 ${isActive(`/${idSucursal}/Contacto`)
                                        ? 'active-nav-link'
                                        : ''
                                        }`}
                                >
                                    Contacto
                                </Button>
                            </Link>
                        </Nav>

                        {/* Carrito derecha */}
                        <div className="d-flex align-items-center">
                            <button
                                onClick={showCar}
                                title='Carrito de compra'
                                className={`cart-button-minimal ${cartBounce ? 'cart-bounce' : ''}`}
                            >
                                <Bag size={22} className='color-secundary' />

                                {totalItems > 0 && (
                                    <span className="cart-badge">
                                        {totalItems}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>

                </Container>
            </Navbar>

            {/* ================= OFFCANVAS ================= */}
            <Offcanvas
                show={menuOpen}
                onHide={toggleMenu}
                placement="start"
                style={{ backgroundColor: '#2E0205' }}
            >
                <Offcanvas.Header closeButton closeVariant="white">
                    <Offcanvas.Title className="color-secundary">
                        Menú
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <Nav className="flex-column">

                        <Link to={`/${idSucursal}`} className="text-decoration-none">
                            <Button
                                className="nav-link color-secundary fw-semibold fs-6 text-start w-100"
                                onClick={redirectToRoot}
                            >
                                Inicio
                            </Button>
                        </Link>

                        <Link to={`/${idSucursal}/Catalogo`} className="text-decoration-none">
                            <Button className="nav-link color-secundary fw-semibold fs-6 text-start w-100">
                                Categorias
                            </Button>
                        </Link>

                        <Link to={`/${idSucursal}/SobreNosotros`} className="text-decoration-none">
                            <Button className="nav-link color-secundary fw-semibold fs-6 text-start w-100">
                                Sobre Nosotros
                            </Button>
                        </Link>

                        <Link to={`/${idSucursal}/Contacto`} className="text-decoration-none">
                            <Button className="nav-link color-secundary fw-semibold fs-6 text-start w-100">
                                Contacto
                            </Button>
                        </Link>
                    </Nav>

                    <hr className="color-secundary my-4" />

                    {/* Redes */}
                    <div className="d-flex justify-content-around">
                        {socialLinks.map((link, index) => (
                            <a
                                key={index}
                                href={link.url}
                                rel='noreferrer noopener'
                                title={link.title}
                            >
                                <Button variant="link" className="color-secundary">
                                    {getIcon(link.url)}
                                </Button>
                            </a>
                        ))}
                    </div>

                </Offcanvas.Body>
            </Offcanvas>

            {showFloatingCartButton && (
                <button
                    onClick={showCar}
                    onPointerDown={handleFloatingButtonPointerDown}
                    title='Carrito de compra'
                    className={`cart-button-minimal ${cartBounce ? 'cart-bounce' : ''}`}
                    style={{
                        position: 'fixed',
                        left: `${floatingPosition.x}px`,
                        top: `${floatingPosition.y}px`,
                        zIndex: 900,
                        width: '45px',
                        height: '45px',
                        borderRadius: '50%',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
                        backgroundColor: '#2E0205',
                        touchAction: 'none',
                        cursor: isDragging ? 'grabbing' : 'grab'
                    }}
                >
                    <Bag size={22} className='color-secundary' />
                    {totalItems > 0 && (
                        <span className="cart-badge" style={{ top: '4px', right: '4px' }}>
                            {totalItems}
                        </span>
                    )}
                </button>
            )}

            {/* Carrito */}
            <CarUni show={show} handleClose={closeCar} />
        </div>
    );
}

export default NavbarUni;
